import React from "react";
import { AA } from "@/constants/testIds";

// Approximate coords for east coast states, scaled to a 0-100 viewport.
// We project longitude (-83..-66) → X (5..95) and latitude (33..47.5) → Y (95..5).
const states = [
  { id: "VA", name: "Virginia", lat: 37.5, lon: -78.0 },
  { id: "MD", name: "Maryland", lat: 39.0, lon: -76.7 },
  { id: "DE", name: "Delaware", lat: 38.9, lon: -75.5 },
  { id: "PA", name: "Pennsylvania", lat: 40.9, lon: -77.5 },
  { id: "NJ", name: "New Jersey", lat: 40.1, lon: -74.5 },
  { id: "NY", name: "New York", lat: 42.9, lon: -75.0 },
  { id: "CT", name: "Connecticut", lat: 41.6, lon: -72.7 },
  { id: "RI", name: "Rhode Island", lat: 41.7, lon: -71.5 },
  { id: "MA", name: "Massachusetts", lat: 42.3, lon: -71.8 },
  { id: "NH", name: "New Hampshire", lat: 43.7, lon: -71.6 },
  { id: "VT", name: "Vermont", lat: 44.1, lon: -72.7 },
  { id: "ME", name: "Maine", lat: 45.4, lon: -69.2 },
];

const HUB = { name: "Philadelphia (Hub)", lat: 39.95, lon: -75.16 };

const project = ({ lat, lon }) => {
  const x = ((lon + 83) / (83 - 66)) * 90 + 5;
  const y = ((47.5 - lat) / (47.5 - 33)) * 90 + 5;
  return { x, y };
};

export default function Coverage() {
  const hubP = project(HUB);
  return (
    <section
      id="coverage"
      data-testid={AA.coverageMap}
      className="relative bg-[#0A0A0A] py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="label-mono mb-5">05 / Coverage Grid</div>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Virginia to Maine.<br />
              <span className="text-[#0066FF]">One control network.</span>
            </h2>
          </div>
          <p className="lg:col-span-6 text-white/60 text-[15px] leading-relaxed self-end">
            Crews staged along the I-95 corridor and inland to Vermont — most projects
            mobilize within 24–48 hours. Outside the grid? We still travel — just tell us
            where.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-0 hairline">
          {/* Map svg */}
          <div className="lg:col-span-8 relative aspect-[4/3] lg:aspect-auto lg:min-h-[520px] bg-[#070707] reticle map-sky overflow-hidden">
            {/* Grid */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
              {/* fine grid */}
              {Array.from({ length: 19 }).map((_, i) => (
                <line key={`v${i}`} x1={(i + 1) * 5} y1="0" x2={(i + 1) * 5} y2="100" stroke="rgba(255,255,255,0.04)" strokeWidth="0.1" />
              ))}
              {Array.from({ length: 19 }).map((_, i) => (
                <line key={`h${i}`} x1="0" y1={(i + 1) * 5} x2="100" y2={(i + 1) * 5} stroke="rgba(255,255,255,0.04)" strokeWidth="0.1" />
              ))}

              {/* Connection lines from hub to each state */}
              {states.map((s) => {
                const p = project(s);
                return (
                  <line
                    key={`l-${s.id}`}
                    x1={hubP.x}
                    y1={hubP.y}
                    x2={p.x}
                    y2={p.y}
                    stroke="rgba(255,69,0,0.32)"
                    strokeWidth="0.18"
                    strokeDasharray="0.6 0.4"
                  />
                );
              })}

              {/* Coverage radius circle (~600mi at this scale, approx 18 viewport units) */}
              <circle cx={hubP.x} cy={hubP.y} r="22" fill="none" stroke="rgba(255,69,0,0.18)" strokeWidth="0.18" strokeDasharray="0.8 0.4" />
              <circle cx={hubP.x} cy={hubP.y} r="14" fill="none" stroke="rgba(255,69,0,0.28)" strokeWidth="0.18" />

              {/* State dots */}
              {states.map((s) => {
                const p = project(s);
                return (
                  <g key={s.id}>
                    <circle cx={p.x} cy={p.y} r="0.7" fill="#FFFFFF" />
                    <circle cx={p.x} cy={p.y} r="1.4" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.18" />
                  </g>
                );
              })}

              {/* Hub */}
              <g>
                <circle cx={hubP.x} cy={hubP.y} r="1.3" fill="#FF4500" />
                <circle cx={hubP.x} cy={hubP.y} r="3" fill="none" stroke="#FF4500" strokeWidth="0.25" />
                <circle cx={hubP.x} cy={hubP.y} r="5" fill="none" stroke="rgba(255,69,0,0.5)" strokeWidth="0.18" strokeDasharray="0.4 0.4" />
              </g>
            </svg>

            {/* State labels via HTML for crisp text */}
            {states.map((s) => {
              const p = project(s);
              return (
                <div
                  key={`lbl-${s.id}`}
                  className="absolute -translate-x-1/2 translate-y-2 font-mono text-[10px] tracking-[0.16em] text-white/70"
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  {s.id}
                </div>
              );
            })}

            {/* Hub label */}
            <div
              className="absolute -translate-x-1/2 -translate-y-7 font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF4500] whitespace-nowrap"
              style={{ left: `${hubP.x}%`, top: `${hubP.y}%` }}
            >
              ▲ HUB · PHL
            </div>

            {/* Corner readouts */}
            <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
              EAST COAST · USA
            </div>
            <div className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
              EPSG:4326
            </div>
            <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
              R · 600 mi
            </div>
            <div className="absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
              12 states · 1 hub
            </div>
          </div>

          {/* States legend */}
          <div className="lg:col-span-4 bg-[#121212] border-t lg:border-t-0 lg:border-l border-white/10">
            <div className="p-6 border-b border-white/10">
              <div className="label-mono mb-2">Coverage Hub</div>
              <div className="font-display font-bold text-white text-xl">Philadelphia, PA</div>
              <div className="font-mono text-[12px] text-white/50 mt-1">
                39.9526°N · 75.1652°W
              </div>
            </div>
            <div className="divide-y divide-white/5">
              {states.map((s, i) => (
                <div key={s.id} className="flex items-center justify-between px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-white/40 w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-bold text-white text-[15px]">{s.name}</span>
                  </div>
                  <span className="font-mono text-[11px] tracking-[0.18em] text-[#FF4500]">
                    {s.id}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
